export interface PrepositionFlashcard {
  id: string;
  category: "Lieu" | "Temps" | "Cause" | "But" | "Moyen" | "Relation" | "Opposition" | "Conséquence" | string;
  preposition: string;
  front: string;
  back: string;
  example: string;
  tags: string[];
}

export const frenchPrepositionFlashcards: PrepositionFlashcard[] = [
  // Lieu
  {
    id: "prep-a-lieu",
    category: "Lieu",
    preposition: "à",
    front: "Como usar a preposição « à » para indicar lugar/destino?",
    back: "Usa-se « à » antes de nomes de cidades, ilhas singulares ou lugares específicos.\n\nExemples:\n• Je vais à Montréal.\n• Il habite à Paris.\n• Elle étudie à l'université.",
    example: "Je vais à Québec pour passer mon examen.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-en-lieu",
    category: "Lieu",
    preposition: "en",
    front: "Quando usar « en » para países e regiões?",
    back: "Usa-se « en » para países femininos (que terminam em 'e') ou países masculinos que começam com vogal.\n\nExemples:\n• Je vais en France.\n• Il habite en Iran.\n• Nous voyageons en Europe.",
    example: "J'aimerais m'installer en Ontario ou en Colombie-Britannique.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-au-lieu",
    category: "Lieu",
    preposition: "au / aux",
    front: "Quando usar « au » e « aux » para países?",
    back: "« Au » é usado para países masculinos no singular (ex: au Canada, au Japon). « Aux » é usado para países no plural (ex: aux États-Unis, aux Pays-Bas).\n\nExemples:\n• Il habite au Canada.\n• Elle va aux États-Unis.",
    example: "Bienvenue au Canada pour votre projet d'immigration.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-dans-lieu",
    category: "Lieu",
    preposition: "dans",
    front: "Qual o significado espacial de « dans »?",
    back: "« Dans » significa 'dentro de' ou no interior de um espaço fechado/delimitado.\n\nExemples:\n• Le livre est dans le sac.\n• Entrez dans la salle d'examen.",
    example: "Les documents se trouvent dans le tiroir du bureau.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-sur-lieu",
    category: "Lieu",
    preposition: "sur",
    front: "Como usar « sur » no sentido de localização ou assunto?",
    back: "« Sur » indica superfície ('sobre', 'em cima de') ou o tópico/assunto de algo.\n\nExemples:\n• Le dossier est sur la table.\n• Une conférence sur l'économie.",
    example: "Posez votre stylo sur la table à la fin de l'épreuve.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-sous-lieu",
    category: "Lieu",
    preposition: "sous",
    front: "Qual a função da preposição « sous »?",
    back: "« Sous » significa 'sob', 'embaixo de' ou sujeito a uma condição.\n\nExemples:\n• Le chat est sous la chaise.\n• Sous le contrôle d'un superviseur.",
    example: "Les résultats seront publiés sous dix jours.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-chez-lieu",
    category: "Lieu",
    preposition: "chez",
    front: "O que expressa a preposição « chez »?",
    back: "« Chez » indica a casa de alguém, o local de trabalho de um profissional (médico, dentista) ou no convívio/natureza de uma pessoa ou grupo.\n\nExemples:\n• Je vais chez le médecin.\n• Chez les Canadiens, le hockey est populaire.",
    example: "Rendez-vous chez le notaire pour signer le contrat.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-devant-lieu",
    category: "Lieu",
    preposition: "devant",
    front: "Qual o uso de « devant »?",
    back: "« Devant » expressa localização física em frente a algo ('diante de', 'na frente de').\n\nExemples:\n• Attends-moi devant l'entrée.\n• Ne stationnez pas devant le garage.",
    example: "Les candidats font la queue devant la salle d'examen.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-derriere-lieu",
    category: "Lieu",
    preposition: "derrière",
    front: "O que significa « derrière »?",
    back: "« Derrière » indica a posição na parte de trás ('atrás de').\n\nExemples:\n• Le parking est derrière le bâtiment.\n• Regarde derrière toi.",
    example: "Le jardin se situe juste derrière la bibliothèque municipale.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-pres-de-lieu",
    category: "Lieu",
    preposition: "près de",
    front: "Como utilizar a locução « près de »?",
    back: "« Près de » significa 'perto de', 'próximo a'. Exige a contração com o artigo (près du, près des).\n\nExemples:\n• J'habite près du métro.\n• L'hôtel est près de la gare.",
    example: "Nous cherchons un appartement situé près du centre-ville.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-loin-de-lieu",
    category: "Lieu",
    preposition: "loin de",
    front: "Como utilizar a locução « loin de »?",
    back: "« Loin de » significa 'longe de'. Também contrai com o artigo definido (loin du, loin des).\n\nExemples:\n• L'aéroport est loin de la ville.\n• Loin des yeux, loin du cœur.",
    example: "Ce quartier calme est situé loin de la pollution industrielle.",
    tags: ["préposition", "lieu", "TEF"]
  },
  {
    id: "prep-vers-lieu",
    category: "Lieu",
    preposition: "vers",
    front: "Qual o sentido de « vers » (lugar)?",
    back: "« Vers » indica direção ('em direção a', 'para as bandas de').\n\nExemples:\n• Allez vers le nord.\n• Tournez vers la gauche.",
    example: "Dirigez-vous vers la sortie de secours en cas d'alarme.",
    tags: ["préposition", "lieu", "TEF"]
  },

  // Temps
  {
    id: "prep-pendant-temps",
    category: "Temps",
    preposition: "pendant",
    front: "Como expressar duração temporal com « pendant »?",
    back: "« Pendant » indica uma duração delimitada no tempo ('durante'). O evento já terminou ou tem duração fixa.\n\nExemples:\n• J'ai étudié pendant trois heures.\n• Pendant les vacances, je me repose.",
    example: "L'épreuve de compréhension orale dure pendant 40 minutes.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-depuis-temps",
    category: "Temps",
    preposition: "depuis",
    front: "Qual a diferença de « depuis » em relação a outras preposições de tempo?",
    back: "« Depuis » indica uma ação que começou no passado e ainda continua no presente ('desde', 'há').\n\nExemples:\n• J'apprends le français depuis six mois.\n• Il habite ici depuis 2020.",
    example: "Je me prépare pour l'examen TEF Canada depuis le mois de janvier.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-en-temps",
    category: "Temps",
    preposition: "en (temps)",
    front: "Quando usar « en » para indicar tempo?",
    back: "« En » expressa o tempo necessário para realizar uma ação ou o mês/ano/estação do ano.\n\nExemples:\n• Il a fini le test en 30 minutes.\n• En été, en 2025, en mai.",
    example: "Vous devez rédiger votre lettre en moins d'une heure.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-dans-temps",
    category: "Temps",
    preposition: "dans (temps)",
    front: "Quando usar « dans » em contextos temporais?",
    back: "« Dans » indica o momento futuro em que uma ação vai acontecer ('daqui a').\n\nExemples:\n• Le train part dans 10 minutes.\n• Je passerai l'examen dans deux semaines.",
    example: "Les inscriptions ouvriront dans quelques jours.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-pour-temps",
    category: "Temps",
    preposition: "pour (temps)",
    front: "Como usar « pour » com sentido temporal?",
    back: "« Pour » indica uma duração prevista ou planejada no futuro ('por', 'para').\n\nExemples:\n• Je pars au Canada pour deux ans.\n• J'ai loué une voiture pour la fin de semaine.",
    example: "Mon visa d'études est valide pour une durée de trois ans.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-avant-temps",
    category: "Temps",
    preposition: "avant",
    front: "Qual a regra gramatical ao usar « avant »?",
    back: "« Avant » significa 'antes de'. Pode ser seguido por um substantivo ou por um verbo no infinitivo (usando « avant de »).\n\nExemples:\n• Arrivez avant 8h.\n• Avant de partir, vérifiez vos papiers.",
    example: "Relisez attentivement votre copie avant de la remettre à l'examinateur.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-apres-temps",
    category: "Temps",
    preposition: "après",
    front: "Qual a regra gramatical ao usar « après »?",
    back: "« Après » significa 'depois de'. Pode ser seguido por substantivo ou pelo infinitivo passado (ex: après avoir fini, après être parti).\n\nExemples:\n• Après l'examen, on fêtera.\n• Après avoir lu le texte, répondez aux questions.",
    example: "Après avoir obtenu le niveau B2, il a soumis son dossier d'immigration.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-des-temps",
    category: "Temps",
    preposition: "dès",
    front: "Qual o significado da preposição « dès »?",
    back: "« Dès » expressa o ponto de partida temporal imediato ('a partir de', 'logo em').\n\nExemples:\n• Dès demain matin.\n• Dès mon arrivée à l'aéroport.",
    example: "Vous recevrez une notification dès la publication des notes.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-jusque-temps",
    category: "Temps",
    preposition: "jusqu'à",
    front: "Como exprimir o limite temporal com « jusqu'à »?",
    back: "« Jusqu'à » significa 'até'. Exige contração com artigos (jusqu'au, jusqu'aux).\n\nExemples:\n• La bibliothèque est ouverte jusqu'à 20h.\n• J'ai travaillé jusqu'au soir.",
    example: "Les candidats ont jusqu'au 15 du mois pour confirmer leur présence.",
    tags: ["préposition", "temps", "TEF"]
  },
  {
    id: "prep-vers-temps",
    category: "Temps",
    preposition: "vers (temps)",
    front: "Qual o uso de « vers » para indicar horário?",
    back: "« Vers » indica um horário aproximado ('por volta de', 'cerca de').\n\nExemples:\n• On se retrouve vers midi.\n• Le vol arrivera vers 17h.",
    example: "La session d'expression orale commencera vers 14h30.",
    tags: ["préposition", "temps", "TEF"]
  },

  // Cause
  {
    id: "prep-a-cause-de-cause",
    category: "Cause",
    preposition: "à cause de",
    front: "Qual a conotação de « à cause de »?",
    back: "« À cause de » introduz uma causa com conotação neutra ou negativa ('por causa de').\n\nExemples:\n• Le vol est annulé à cause de la tempête.\n• Il a échoué à cause du stress.",
    example: "Le trafic est ralenti à cause de travaux sur l'autoroute.",
    tags: ["préposition", "cause", "TEF"]
  },
  {
    id: "prep-grace-a-cause",
    category: "Cause",
    preposition: "grâce à",
    front: "Qual a conotação de « grâce à »?",
    back: "« Grâce à » introduz uma causa com conotação positiva ou benéfica ('graças a').\n\nExemples:\n• J'ai réussi mon TEF grâce à mes efforts.\n• Grâce aux conseils du professeur.",
    example: "Elle a obtenu son visa de résident permanent grâce à son excellent score en français.",
    tags: ["préposition", "cause", "TEF"]
  },
  {
    id: "prep-en-raison-de-cause",
    category: "Cause",
    preposition: "en raison de",
    front: "Como usar « en raison de » no contexto profissional/administrativo?",
    back: "« En raison de » é uma locução formal muito usada em comunicados oficiais para expressar a causa ('em virtude de', 'devido a').\n\nExemples:\n• En raison d'un problème technique.\n• En raison des conditions météorologiques.",
    example: "Le centre sera fermé exceptionnellement en raison d'un jour férié.",
    tags: ["préposition", "cause", "TEF"]
  },
  {
    id: "prep-faute-de-cause",
    category: "Cause",
    preposition: "faute de",
    front: "O que exprime a locução « faute de »?",
    back: "« Faute de » exprime a falta ou ausência de algo necessário ('por falta de').\n\nExemples:\n• Faute de temps, je n'ai pas terminé.\n• Faute de preuves suffisantes.",
    example: "Faute de places disponibles, l'inscription a été reportée à la session suivante.",
    tags: ["préposition", "cause", "TEF"]
  },

  // But
  {
    id: "prep-pour-but",
    category: "But",
    preposition: "pour",
    front: "Como expressar objetivo com « pour »?",
    back: "« Pour » seguido de infinitivo ou substantivo expressa finalidade ('para', 'com o intuito de').\n\nExemples:\n• Il étudie pour obtenir son diplôme.\n• Un manuel pour la préparation du test.",
    example: "Je lis régulièrement des articles de presse pour enrichir mon vocabulaire.",
    tags: ["préposition", "but", "TEF"]
  },
  {
    id: "prep-afin-de-but",
    category: "But",
    preposition: "afin de",
    front: "Em que nível de formalidade se usa « afin de »?",
    back: "« Afin de » é uma expressão formal equivalente a « pour » ('a fim de', 'para'). Exige verbo no infinitivo.\n\nExemples:\n• Afin de garantir la sécurité des données.\n• Afin de mieux vous servir.",
    example: "Veuillez présenter une pièce d'identité valide afin de valider votre inscription.",
    tags: ["préposition", "but", "TEF"]
  },
  {
    id: "prep-en-vue-de-but",
    category: "But",
    preposition: "en vue de",
    front: "Qual o sentido de « en vue de »?",
    back: "« En vue de » exprime uma preparação com vistas a um objetivo futuro ('tendo em vista', 'com o propósito de').\n\nExemples:\n• En vue de l'examen final.\n• En vue d'une embauche prochaine.",
    example: "Il suit un stage intensif en vue de son intégration professionnelle au Québec.",
    tags: ["préposition", "but", "TEF"]
  },

  // Moyen / Manière
  {
    id: "prep-par-moyen",
    category: "Moyen",
    preposition: "par",
    front: "Quais os principais usos de « par »?",
    back: "« Par » indica o meio, o canal de envio ou distribuição, ou o agente da voz passiva ('por', 'através de').\n\nExemples:\n• Envoyer un document par courriel.\n• Divisé par quatre.\n• Le texte est écrit par Victor Hugo.",
    example: "Vous recevrez votre attestation officielle par voie électronique.",
    tags: ["préposition", "moyen", "TEF"]
  },
  {
    id: "prep-avec-moyen",
    category: "Moyen",
    preposition: "avec",
    front: "Como usar « avec » para indicar instrumento ou maneira?",
    back: "Além de companhia, « avec » expressa o instrumento ou o modo como algo é feito ('com').\n\nExemples:\n• Écrire avec un stylo bleu.\n• Travailler avec enthousiasme.",
    example: "Traitez chaque question avec soin et attention.",
    tags: ["préposition", "moyen", "TEF"]
  },
  {
    id: "prep-sans-maniere",
    category: "Moyen",
    preposition: "sans",
    front: "O que expressa a preposição « sans »?",
    back: "« Sans » expressa a ausência ou privação ('sem').\n\nExemples:\n• Un café sans sucre.\n• Répondre sans hésiter.",
    example: "Le candidat s'est exprimé couramment et sans faire de fautes de syntaxe.",
    tags: ["préposition", "moyen", "TEF"]
  },

  // Relation
  {
    id: "prep-de-relation",
    category: "Relation",
    preposition: "de",
    front: "Quais os principais papéis da preposição « de »?",
    back: "« De » indica posse, origem, matéria ou o assunto de algo ('de', 'do'). Contrai com artigos (du, des).\n\nExemples:\n• Le livre du professeur.\n• Un cours de français.\n• Parler de l'examen.",
    example: "L'attestation de résultats est indispensable pour le dossier d'immigration.",
    tags: ["préposition", "relation", "TEF"]
  },
  {
    id: "prep-entre-relation",
    category: "Relation",
    preposition: "entre",
    front: "Como usar « entre »?",
    back: "« Entre » expressa a relação ou oposição entre duas ou mais coisas/pessoas ('entre').\n\nExemples:\n• Choisir entre deux options.\n• Un accord entre le Canada et la France.",
    example: "Il y a une grande différence de sens entre ces deux verbes.",
    tags: ["préposition", "relation", "TEF"]
  },
  {
    id: "prep-parmi-relation",
    category: "Relation",
    preposition: "parmi",
    front: "Qual a diferença entre « entre » e « parmi »?",
    back: "« Parmi » significa 'no meio de' um grupo maior (mais de duas entidades, ou um conjunto incontável/coletivo).\n\nExemples:\n• Parmi les candidats, beaucoup sont bilingues.\n• Il a été choisi parmi des centaines de postulants.",
    example: "Sélectionnez la bonne réponse parmi les quatre choix proposés.",
    tags: ["préposition", "relation", "TEF"]
  },
  {
    id: "prep-contre-relation",
    category: "Relation",
    preposition: "contre",
    front: "O que expressa a preposição « contre »?",
    back: "« Contre » expressa oposição, contato físico ou troca ('contra').\n\nExemples:\n• Voter contre le projet.\n• Échanger un bon contre un billet.",
    example: "Les arguments avancés contre cette mesure économique sont très solides.",
    tags: ["préposition", "relation", "TEF"]
  },
  {
    id: "prep-envers-relation",
    category: "Relation",
    preposition: "envers",
    front: "Qual o uso de « envers »?",
    back: "« Envers » expressa a atitude ou o comportamento moral em relação a alguém ('para com', 'em relação a').\n\nExemples:\n• Être poli envers ses collègues.\n• Ses responsabilidades envers la communauté.",
    example: "L'enseignant fait preuve d'une grande bienveillance envers ses étudiants.",
    tags: ["préposition", "relation", "TEF"]
  },
  {
    id: "prep-concernant-relation",
    category: "Relation",
    preposition: "concernant",
    front: "Como usar « concernant »?",
    back: "« Concernant » é sinônimo de « à propos de » ou « sur » ('no que diz respeito a', 'sobre').\n\nExemples:\n• Des questions concernant le règlement.\n• Une annonce concernant les visas.",
    example: "Pour toute question concernant l'évaluation, adressez-vous au secrétariat.",
    tags: ["préposition", "relation", "TEF"]
  },

  // Opposition / Concession / Autre
  {
    id: "prep-malgre-opposition",
    category: "Opposition",
    preposition: "malgré",
    front: "Como expressar concessão com « malgré »?",
    back: "« Malgré » exprime concessão ou oposição ineficaz ('apesar de'). Sempre seguido por um substantivo.\n\nExemples:\n• Malgré la pluie, nous sortons.\n• Malgré la difficulté, il a réussi.",
    example: "Elle a obtenu un excellent résultat au TEF malgré son manque de temps pour étudier.",
    tags: ["préposition", "opposition", "TEF"]
  },
  {
    id: "prep-au-lieu-de-opposition",
    category: "Opposition",
    preposition: "au lieu de",
    front: "Como usar a locução « au lieu de »?",
    back: "« Au lieu de » exprime substituição ou oposição de escolhas ('em vez de', 'ao invés de'). Seguido por substantivo ou verbo no infinitivo.\n\nExemples:\n• Prends le bus au lieu de la voiture.\n• Étudie au lieu de regarder la télé.",
    example: "Utilisez un vocabulaire précis au lieu de termes trop familiers dans votre rédaction.",
    tags: ["préposition", "opposition", "TEF"]
  },
  {
    id: "prep-a-lexception-de-opposition",
    category: "Opposition",
    preposition: "à l'exception de",
    front: "O que exprime « à l'exception de »?",
    back: "« À l'exception de » exprime exclusão ('com exceção de', 'exceto').\n\nExemples:\n• Tout est ouvert à l'exception du dimanche.\n• Tous les candidats ont réussi, à l'exception d'un seul.",
    example: "Le règlement s'applique à tous les départements, à l'exception du service informatique.",
    tags: ["préposition", "opposition", "TEF"]
  },
  {
    id: "prep-sauf-opposition",
    category: "Opposition",
    preposition: "sauf",
    front: "O que exprime a preposição « sauf »?",
    back: "« Sauf » é o equivalente curto e direto a 'salvo', 'exceto'.\n\nExemples:\n• Ouvert tous les jours sauf le lundi.\n• Sauf erreur de ma part.",
    example: "Le port du masque est recommandé, sauf pendant l'épreuve orale.",
    tags: ["préposition", "opposition", "TEF"]
  }
];
